/**
 * BTB Hub delivery date engine.
 *
 * Pure date arithmetic, no DOM, no clock of its own — the caller supplies
 * `nowUtc`. That is what makes the Friday / weekend / bank-holiday matrix
 * testable without waiting for a Friday. See tests/btb-delivery-engine.test.js.
 *
 * Why this runs in the browser and not in Liquid: Shopify full-page-caches
 * product pages, so Liquid's `'now'` is evaluated when the page is CACHED, not
 * when it is VIEWED. A Liquid-computed date can be days stale — potentially in
 * the past. There is no correct server-side option.
 *
 * The rule (approved 2026-08-11):
 *   dispatch day = today, if today is a working day AND UK time is before the
 *                  cutoff; otherwise the next working day
 *   delivery from = dispatch day + leadDays working days
 *   working day  = Mon-Fri excluding England & Wales bank holidays
 *
 * UMD-wrapped so Node can require() it for tests while the theme gets a global.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.BTBDeliveryEngine = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // England & Wales only — BTB dispatches from England, so these are the dates
  // the carriers observe. Scottish (2 Jan, early Aug) and Northern Irish
  // (17 Mar, 12 Jul) holidays are deliberately excluded.
  //
  // 2026-2028: confirmed from gov.uk/bank-holidays.json, fetched 2026-08-11.
  // 2029-2031: derived from statutory rules (Easter via Meeus/Jones/Butcher,
  //   first/last Monday rules, weekend substitution) using a generator
  //   validated to reproduce 2026-2028 exactly against the feed. gov.uk only
  //   publishes a rolling window ending 2028, so these cannot be sourced.
  //
  // LIMITATION: derived years cannot predict one-off national holidays — the
  // 2022 Platinum Jubilee, the 2023 Coronation, the Queen's State Funeral. If
  // one is announced for 2029+, add the date by hand. They are announced a
  // year or more ahead.
  var BANK_HOLIDAYS = [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
    '2029-01-01', '2029-03-30', '2029-04-02', '2029-05-07', '2029-05-28', '2029-08-27', '2029-12-25', '2029-12-26',
    '2030-01-01', '2030-04-19', '2030-04-22', '2030-05-06', '2030-05-27', '2030-08-26', '2030-12-25', '2030-12-26',
    '2031-01-01', '2031-04-11', '2031-04-14', '2031-05-05', '2031-05-26', '2031-08-25', '2031-12-25', '2031-12-26'
  ];

  var LAST_HOLIDAY_YEAR = 2031;

  // Deliberately not Intl month names: en-GB renders September as "Sept",
  // which sits inconsistently next to "Aug" and "Dec". Fixed arrays give
  // deterministic three-letter output regardless of the browser's ICU build.
  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var MS_PER_DAY = 86400000;

  /**
   * UK wall-clock date and hour for a given instant.
   * Uses Intl with an explicit timeZone rather than getHours(), so the cutoff
   * is 12:00 in London regardless of the shopper's device timezone, and
   * BST/GMT is handled with no offset table to go stale.
   */
  function ukParts(nowUtc) {
    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hourCycle: 'h23'
    });
    var out = {};
    fmt.formatToParts(nowUtc).forEach(function (p) {
      if (p.type === 'year') { out.year = parseInt(p.value, 10); }
      if (p.type === 'month') { out.month = parseInt(p.value, 10); }
      if (p.type === 'day') { out.day = parseInt(p.value, 10); }
      if (p.type === 'hour') { out.hour = parseInt(p.value, 10); }
    });
    return out;
  }

  // Represent a calendar date as UTC midnight so day arithmetic can never be
  // bitten by a DST transition.
  function toUtcMidnight(p) {
    return new Date(Date.UTC(p.year, p.month - 1, p.day));
  }

  function iso(d) {
    return d.toISOString().slice(0, 10);
  }

  function addDays(d, n) {
    return new Date(d.getTime() + n * MS_PER_DAY);
  }

  function isWorkingDay(d, holidays) {
    var day = d.getUTCDay();
    if (day === 0 || day === 6) { return false; }
    return holidays.indexOf(iso(d)) === -1;
  }

  function nextWorkingDay(d, holidays) {
    var r = addDays(d, 1);
    while (!isWorkingDay(r, holidays)) { r = addDays(r, 1); }
    return r;
  }

  function computeDeliveryDate(opts) {
    var holidays = opts.holidays || BANK_HOLIDAYS;
    var parts = ukParts(opts.nowUtc);

    // Past the baked-in table, fall back to weekends-only. The date is then
    // slightly optimistic rather than computed against an exhausted calendar.
    if (parts.year > LAST_HOLIDAY_YEAR) { holidays = []; }

    var today = toUtcMidnight(parts);

    // Guards on the two numeric inputs. Neither can currently arrive bad — both
    // are `range` settings, which Shopify guarantees to be in-range integers and
    // will not let a merchant blank — but they carry a delivery promise, so they
    // are worth three lines of defence against a future schema edit (a renamed
    // setting id yields nil in Liquid, which lands here as null).
    //
    // The failure directions are not symmetric, which is why the defaults differ.
    // A bad cutoff makes `parts.hour < undefined` false, so dispatch always slips
    // to the next working day: a one-day under-promise, the safe direction. A bad
    // leadDays makes the loop below never run, so delivery === dispatch — on a
    // pallet product that silently promises same-day delivery. So when leadDays
    // is unusable, fall back to the SLOWER of the two groups: if we cannot tell
    // what was asked for, promise the later date.
    // Lower bound is 1, not 0, and null/'' are rejected before Number() sees them:
    // Number(null) and Number('') are both 0, which would pass a >= 0 check and
    // become a midnight cutoff — i.e. "never dispatch today", the exact one-day
    // slip the guard exists to prevent. A genuine midnight cutoff is meaningless
    // for a shop and unreachable from the schema (range 8-18), so treating 0 as
    // broken loses nothing. Caught by the unit test, not by reading.
    // Only a number or a numeric string is accepted. Everything else is treated
    // as broken rather than coerced: Number(null), Number('') and Number(false)
    // are all 0, and Number(true) is 1 — which clears a >= 1 floor and becomes a
    // 01:00 cutoff, or a one-day lead on a pallet product. Coercion here fails in
    // the optimistic direction, so it is the wrong default for a delivery promise.
    function numeric(v) {
      if (typeof v === 'number') { return v; }
      if (typeof v === 'string' && v.trim() !== '') { return Number(v); }
      return NaN;
    }

    var cutoffHour = numeric(opts.cutoffHour);
    if (!isFinite(cutoffHour) || cutoffHour < 1 || cutoffHour > 23) { cutoffHour = 12; }

    var leadDays = Math.round(numeric(opts.leadDays));
    if (!isFinite(leadDays) || leadDays < 1 || leadDays > 30) { leadDays = 2; }

    var dispatch = (isWorkingDay(today, holidays) && parts.hour < cutoffHour)
      ? today
      : nextWorkingDay(today, holidays);

    var delivery = dispatch;
    for (var i = 0; i < leadDays; i++) {
      delivery = nextWorkingDay(delivery, holidays);
    }
    return delivery;
  }

  function formatDeliveryDate(d) {
    return DAY_NAMES[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTH_NAMES[d.getUTCMonth()];
  }

  return {
    BANK_HOLIDAYS: BANK_HOLIDAYS,
    LAST_HOLIDAY_YEAR: LAST_HOLIDAY_YEAR,
    computeDeliveryDate: computeDeliveryDate,
    formatDeliveryDate: formatDeliveryDate
  };
});
